import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Divider, useTheme } from '@mui/material';
import { emailNotificationsNotificationsApi } from 'app/services/api';
import Accordion from 'app/shared/components/common/Accordion';
import Skeleton from 'app/shared/components/common/Skeleton';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoading from 'hooks/useLoading';
import { blobToBase64 } from 'utils/file';

import { getEmailBodyAction, readEmailsAction } from 'store/loanApplications/actions';
import { selectActivityById, selectContactsWithBorrowers } from 'store/loanApplications/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { getStyles } from '../../../Activities.styles';
import { getRecipientsWithNames } from '../../../Activities.utils';
import useHtmlAdjustedToTheme from '../../../hooks/useHtmlAdjustedToTheme';

import EmailActions from './EmailActions';
import EmailFooter from './EmailFooter';
import EmailHeader from './EmailHeader';
import EmailMeta from './EmailMeta';

const SingleEmail = ({
  applicationId,
  emailId,
  isExpanded = false,
  onEmailToggle,
  isBodyLoading,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = getStyles(theme);

  const highlightText = useSelector(getDebouncedSearchValue);

  const [isLoading, setIsLoading] = useLoading(false);

  const isLightWeightEmailBodyAllowed = useFeatureToggle(FeatureFlag.EnableLightWeightEmailBody);

  const contacts = useSelector(selectContactsWithBorrowers);
  const email = useSelector(selectActivityById(emailId));

  const {
    id,
    body,
    events,
    to,
    cc,
    bcc,
    from,
    isNewEmail,
    attachmentNames,
    linkedResourceDescriptions,
  } = email;

  const isNew = isNewEmail;

  const [linkedStorage, setLinkedStorage] = useState({});

  const [isLinkedResourcesLoaded, setIsLinkedResourcesLoaded] = useState(false);

  const emailContent = useHtmlAdjustedToTheme(body, linkedStorage, highlightText);
  const isContentLoading = isLightWeightEmailBodyAllowed ? isLoading : isBodyLoading;

  const emailContentSx = useMemo(
    () => ({ ...classes.emailContent, ...classes.themeTextColor }),
    [classes.emailContent, classes.themeTextColor],
  );
  const accordionSx = useMemo(
    () => ({ ...classes.emailItem, ...classes.accordion }),
    [classes.emailItem, classes.accordion],
  );

  const recipientsString = useMemo(
    () => to && getRecipientsWithNames({ contacts, receivers: to }),
    [contacts, to],
  );

  const toggleIsExpanded = useCallback(() => {
    const isReadingEmailAllowed = !isExpanded && isNew && !isLightWeightEmailBodyAllowed;

    if (isReadingEmailAllowed) {
      setIsLoading(true);

      dispatch(readEmailsAction([id]))
        .catch(Error)
        .finally(() => setIsLoading(false));
    }

    onEmailToggle(emailId);
  }, [
    dispatch,
    id,
    isExpanded,
    isNew,
    setIsLoading,
    onEmailToggle,
    emailId,
    isLightWeightEmailBodyAllowed,
  ]);

  // images in emails contain src="cid-{guid}", should be requested by guid by separate request
  // blob is converted to base64 and set to image's src
  useEffectSafeStrictMode(() => {
    if (!isExpanded || !linkedResourceDescriptions?.length || isLinkedResourcesLoaded) {
      return;
    }

    linkedResourceDescriptions.forEach(resourceDescription => {
      emailNotificationsNotificationsApi
        .getLinkedResource({ emailId: id, resourceId: resourceDescription.resourceId })
        .then(data => {
          blobToBase64(data.data).then(binary => {
            setLinkedStorage(storage => ({
              ...storage,
              [resourceDescription.resourceId]: binary,
            }));
          });
        });
    });

    setIsLinkedResourcesLoaded(true);
  }, [id, isExpanded, isLinkedResourcesLoaded, linkedResourceDescriptions]);

  useEffectSafeStrictMode(() => {
    const isBodyContentLoaded = !!body;
    const isBodyContentLoadAllowed =
      isExpanded && !isBodyContentLoaded && !isLoading && isLightWeightEmailBodyAllowed;

    if (isBodyContentLoadAllowed) {
      setIsLoading(true);
      Promise.all([dispatch(getEmailBodyAction(id)), isNew && dispatch(readEmailsAction([id]))])
        .catch(Error)
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    id,
    isExpanded,
    isNew,
    dispatch,
    setIsLoading,
    body,
    isLoading,
    isLightWeightEmailBodyAllowed,
  ]);

  return (
    <Accordion
      bottom={
        <>
          <Divider />

          <div className="flex items-start">
            <EmailMeta
              bcc={bcc}
              cc={cc}
              contacts={contacts}
              from={from}
              recipientsString={recipientsString}
            />

            <EmailActions activity={email} />
          </div>

          <Divider />

          {isContentLoading && (
            <Skeleton animation="pulse" className="h-20" variant="text" width="100%" />
          )}
          {!isContentLoading && (
            <Box
              className="min-h-36 pt-8 mb-16 break-all"
              dangerouslySetInnerHTML={emailContent}
              sx={emailContentSx}
            />
          )}

          <EmailFooter
            applicationId={applicationId}
            attachmentNames={attachmentNames}
            emailId={id}
            events={events}
          />
        </>
      }
      className="w-full shadow-none"
      defaultExpanded={false}
      expanded={isExpanded}
      id={`email-${id}`}
      isWithDivider={false}
      isWithExpandIcon={false}
      sx={accordionSx}
      top={
        <EmailHeader
          emailId={emailId}
          isBodyLoading={isBodyLoading}
          isCollapsed={!isExpanded}
          isNew={isNew}
        />
      }
      onChange={toggleIsExpanded}
    />
  );
};

SingleEmail.propTypes = {
  applicationId: PropTypes.string.isRequired,
  emailId: PropTypes.string.isRequired,
  isBodyLoading: PropTypes.bool.isRequired,
  onEmailToggle: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
};

export default memo(SingleEmail);
